import { useMutation, useQueryClient } from "@tanstack/react-query";

import keys from "CommissionDistribution/Api/keys";
import {
	type CommissionDistributionResponse,
	type EntityListOfContractResponse,
	putContractsContractIdCommissionDistribution,
	type UpsertCommissionDistributionApiRequest,
} from "Infrastructure/Api/Api";
import { processResponse } from "Infrastructure/Api/ApiUtils";

type UpsertArgs = {
	contractId: string;
	request: UpsertCommissionDistributionApiRequest;
};

const deriveProvisionStatus = (
	response: CommissionDistributionResponse,
): "Ready" | "Pending" | "NotReady" => {
	if (response.isConfirmed) return "Ready";
	if (response.participants.length > 0) return "Pending";
	return "NotReady";
};

export const useUpsertCommissionDistributionMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: processResponse(({ contractId, request }: UpsertArgs) =>
			putContractsContractIdCommissionDistribution(request, contractId),
		),
		onSuccess: (
			response: CommissionDistributionResponse,
			variables: UpsertArgs,
		) => {
			queryClient.setQueryData(
				keys.commissionDistribution(variables.contractId),
				response,
			);

			const provision = deriveProvisionStatus(response);
			queryClient.setQueriesData<EntityListOfContractResponse>(
				{ queryKey: ["contracts"] },
				(old) => {
					if (!old) return old;
					return {
						...old,
						items: old.items.map((c) =>
							c.contractId === variables.contractId
								? {
										...c,
										essentials: { ...c.essentials, provision },
									}
								: c,
						),
					};
				},
			);
		},
	});
};
